import {
  createEntityAdapter,
  createSelector,
  EntityState,
} from '@reduxjs/toolkit';
import {
  BaseQueryFn,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { AssetEntity } from '../asset';
import { authActions } from '../auth';
import type { RootState } from '../root';

const API_URL = (import.meta.env.VITE_API_URL as string) + '/api/v1/';
const mutex = new Mutex();

const baseHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;
    const token = state.auth.user?.token || state.sharedReports.accessToken;
    const csrf = state.auth.xsrfToken;

    // Set default headers
    Object.entries(baseHeaders).forEach(([key, value]) => {
      headers.set(key, value);
    });

    // Handle FormData content type
    const isFormData = headers
      .get('Content-Type')
      ?.includes('multipart/form-data');
    if (isFormData) {
      headers.delete('Content-Type'); // Let browser set it
    }

    // Set auth headers
    if (csrf) headers.set('X-XSRF-TOKEN', csrf);
    if (token) headers.set('Authorization', `Bearer ${token}`);

    return headers;
  },
  credentials: 'include',
});

const customBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  // If we get a 401 error, attempt to refresh the token
  if (result.error?.status === 401) {
    // Lock the mutex so other queries wait until the token refresh is complete
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        api.dispatch(authActions.logout());
      } finally {
        // Release the mutex no matter what happens
        release();
      }
    } else {
      // If another request is refreshing the token, wait for it to complete
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }

  // Handle terms acceptance requirement
  if (
    result?.error?.status === 403 &&
    (result?.error?.data as any)?.terms_update_required
  ) {
    const path = window.location.pathname + window.location.search;
    if (!/\/accept-terms|\/login|\/signup/.test(path)) {
      api.dispatch({
        type: 'terms/requireAcceptance',
        payload: { intendedPath: path },
      });
      window.location.assign('/accept-terms');
    }
    return { error: result.error };
  }

  // Handle 402 Payment Required (subscription required)
  // The TrialExpiredCard component will be shown based on billing data from /api/user
  // This handler prevents unnecessary error propagation while allowing the UI to handle it gracefully
  if (result?.error?.status === 402) {
    // Don't throw error - let the component handle via organisation billing context
    // The TrialExpiredCard is already shown based on billing.is_subscribed, on_trial, and trial_ends_at
    // If we're not on dashboard, redirect there to show the expired trial card
    const path = window.location.pathname;
    if (path !== '/' && !path.startsWith('/organisation/billing')) {
      // Only redirect if not already on dashboard or billing page
      // The dashboard will show the TrialExpiredCard overlay
      window.location.href = '/';
    }
    return { error: result.error };
  }

  return result;
};

const assetsAdapter = createEntityAdapter<AssetEntity>();
const initialState = assetsAdapter.getInitialState();

// New endpoints should be added to a domain file in this directory via
// rAPI.injectEndpoints(...) (see assetsAPI.ts, usersAPI.ts, etc.), not here.
export const rAPI = createApi({
  reducerPath: 'assetApi',
  baseQuery: customBaseQuery,
  tagTypes: [
    'Asset',
    'Assets',
    'CoverImage',
    'Assessments',
    'Assessment',
    'Buildings',
    'Levels',
    'Areas',
    'Building',
    'Level',
    'Area',
    'User',
    'Users',
    'Qualifications',
    'Offices',
    'Organisations',
    'ElementGroups',
    'ElementTree',
    'ElementItems',
    'ElementItem',
    'Recommendations',
    'Observations',
    'TestSuites',
    'Specifications',
    'TestSuiteViews',
    'TestSpecificationViews',
    'ReferenceImages',
    'RatingScales',
    'TestItems',
    'TestItem',
    'Comments',
    'ReportBlocks',

    'Report',
    'Reports',
    'ReportRevision',
    'ReportSections',
    'ReportSection',
    'Notifications',
    'ActiveExports',
    'Dashboard',
    'Activity',
  ],
  endpoints: (builder) => ({
    getAssets: builder.query<EntityState<AssetEntity, number>, void>({
      query: () => 'assets',
      transformResponse: (response: { data: AssetEntity[] }) => {
        //return response.data;
        return assetsAdapter.setAll(initialState, response.data);
      },
      providesTags: (result) =>
        result?.ids
          ? [
              ...result.ids.map((id) => ({ type: 'Asset' as const, id })),
              { type: 'Assets' as const, id: 'LIST' },
            ]
          : [{ type: 'Assets', id: 'LIST' }],
    }),
  }),
});

export const { useGetAssetsQuery } = rAPI;

export const selectAssetsResult = rAPI.endpoints.getAssets.select();

export const selectAssetsData = createSelector(
  selectAssetsResult,
  (result) => result?.data ?? initialState
);

export const { selectAll: selectAllAssets, selectById: selectAssetById } =
  assetsAdapter.getSelectors(selectAssetsData);
